import { useEffect, useSyncExternalStore } from "react";

/**
 * Whether the announcement card is currently on screen, so the app-wide strip can stand down.
 *
 * They are the same offer with the same modal behind the same button. Showing both stacks one on
 * the other, and dismissing the strip there leaves the card in place, which reads as a dismiss
 * button that does not work.
 *
 * Asked of the card rather than derived from the route: a route list has to be updated by whoever
 * adds a fourth surface, and nothing fails if they don't. (The strip still decides the WIZARD route
 * by route, because nothing renders the card there.)
 *
 * A count rather than a boolean because mount order during a route transition is not guaranteed to
 * be unmount-then-mount: a brief overlap would let the first card's cleanup clear a flag the second
 * still needs.
 */

type Listener = () => void;

const listeners = new Set<Listener>();
let mountedCount = 0;

function emit(): void {
    listeners.forEach((listener) => listener());
}

/**
 * Called by whichever component renders the card. Pass `false` when mounted but not showing the
 * offer — hooks cannot be conditional, and a component that returned null must not claim the
 * surface.
 */
export function useAnnounceOfferSurface(isShowing: boolean): void {
    useEffect(() => {
        if (!isShowing) return;
        mountedCount += 1;
        emit();
        return () => {
            // Clamped: `resetOfferSurfaceForTests` zeroes the count while a card may still be
            // mounted, so its cleanup can push the count negative — after which a mounted card
            // reports zero and the stacked-offer bug shows up as a PASSING test.
            mountedCount = Math.max(0, mountedCount - 1);
            emit();
        };
    }, [isShowing]);
}

function subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getSnapshot(): boolean {
    return mountedCount > 0;
}

/** Whether the announcement card is on screen right now. */
export function useOfferSurfaceMounted(): boolean {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Test-only: drop subscribers and reset the count between cases. */
export function resetOfferSurfaceForTests(): void {
    listeners.clear();
    mountedCount = 0;
}
